import { REFRESH_TIME } from '@src/constants/query_keys';
import { updateIsRequesting } from '@src/store/slices/requesting';
import { updateRefreshPdf } from '@src/store/slices/ui';
import type { UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

/**
 * PDF生成の共通設定
 */
interface PdfGenerationConfig<TParams, TResponse> {
  mutationFn: (params: TParams) => Promise<TResponse>;
  onSuccessCallback: (data: TResponse) => void;
  queryKey: readonly unknown[];
  errorQueryKey: readonly unknown[];
}

/**
 * PDF生成フックの戻り値の型
 */
interface PdfGenerationReturn<TParams, TResponse>
  extends Omit<UseMutationResult<TResponse, Error, TParams>, 'mutate' | 'mutateAsync'> {
  generateDebounced: (params: TParams) => void;
  generateImmediate: (params: TParams) => void;
  generateAsync: (params: TParams) => Promise<TResponse>;
}

/**
 * キューに入れるリクエスト
 */
interface QueuedRequest<TParams> {
  params: TParams;
  isDebounced: boolean;
  resolve?: (value: unknown) => void;
  reject?: (error: Error) => void;
}

/**
 * リトライ判定ロジック（共通）
 */
const shouldRetry = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= 1) {
    return false;
  }
  return (
    error instanceof Error &&
    (error.message.includes('network') ||
      error.message.includes('fetch') ||
      error.message.includes('timeout'))
  );
};

/**
 * PDF生成の共通ベースフック
 *
 * usePdfGeneration と useRirekishoPdfGeneration の共通ロジックを提供
 * リクエストは直列に実行され、同時に複数のリクエストが実行されることはありません
 *
 * @param config - PDF生成の設定
 * @returns PDF生成フックの戻り値
 */
export function usePdfGenerationBase<TParams, TResponse>(
  config: PdfGenerationConfig<TParams, TResponse>
): PdfGenerationReturn<TParams, TResponse> {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // リクエストキュー（最新のリクエストのみ保持）
  const pendingRequestRef = useRef<QueuedRequest<TParams> | null>(null);
  // 現在実行中かどうか
  const isProcessingRef = useRef(false);
  // mutation参照を保持
  const mutationRef = useRef<UseMutationResult<TResponse, Error, TParams> | null>(null);
  // 現在実行中のリクエストのPromiseコールバック
  const currentRequestCallbacksRef = useRef<{
    resolve?: (value: unknown) => void;
    reject?: (error: Error) => void;
  } | null>(null);

  /**
   * キューから次のリクエストを処理（refベース）
   */
  const processNextRequest = useCallback(() => {
    if (isProcessingRef.current) {
      return;
    }

    const nextRequest = pendingRequestRef.current;
    if (!nextRequest || !mutationRef.current) {
      return;
    }

    pendingRequestRef.current = null;
    isProcessingRef.current = true;

    // Promiseコールバックを保存
    if (nextRequest.resolve || nextRequest.reject) {
      currentRequestCallbacksRef.current = {
        resolve: nextRequest.resolve,
        reject: nextRequest.reject,
      };
    } else {
      currentRequestCallbacksRef.current = null;
    }

    mutationRef.current.mutate(nextRequest.params);
  }, []);

  const mutationOptions: UseMutationOptions<TResponse, Error, TParams> = {
    mutationFn: config.mutationFn,
    onMutate: () => {
      dispatch(updateIsRequesting(true));
      dispatch(updateRefreshPdf(false));
    },
    onSuccess: (data: TResponse) => {
      config.onSuccessCallback(data);
      queryClient.setQueryData(config.queryKey, data);
      queryClient.removeQueries({ queryKey: config.errorQueryKey });
      // Promiseコールバックを呼び出し
      if (currentRequestCallbacksRef.current?.resolve) {
        currentRequestCallbacksRef.current.resolve(data);
        currentRequestCallbacksRef.current = null;
      }
    },
    onError: (error: Error) => {
      queryClient.setQueryData(config.errorQueryKey, {
        error: error.message,
        timestamp: Date.now(),
      });
      // Promiseコールバックを呼び出し
      if (currentRequestCallbacksRef.current?.reject) {
        currentRequestCallbacksRef.current.reject(error);
        currentRequestCallbacksRef.current = null;
      }
    },
    onSettled: () => {
      dispatch(updateIsRequesting(false));
      isProcessingRef.current = false;
      // キューに次のリクエストがある場合は実行
      processNextRequest();
    },
    retry: shouldRetry,
  };

  const mutation = useMutation(mutationOptions);
  mutationRef.current = mutation;

  /**
   * リクエストをキューに追加（最新のもののみ保持）
   */
  const enqueueRequest = useCallback(
    (
      params: TParams,
      isDebounced: boolean,
      resolve?: (value: unknown) => void,
      reject?: (error: Error) => void
    ) => {
      pendingRequestRef.current = { params, isDebounced, resolve, reject };

      // すでに処理中でなければ、すぐに実行
      if (!isProcessingRef.current) {
        processNextRequest();
      }
    },
    [processNextRequest]
  );

  const generateDebounced = useCallback(
    (params: TParams) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        enqueueRequest(params, true);
      }, REFRESH_TIME);
    },
    [enqueueRequest]
  );

  const generateImmediate = useCallback(
    (params: TParams) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      enqueueRequest(params, false);
    },
    [enqueueRequest]
  );

  /**
   * PDF生成を即座に実行し、完了を待つPromiseを返す
   */
  const generateAsync = useCallback(
    (params: TParams): Promise<TResponse> => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      return new Promise<TResponse>((resolve, reject) => {
        enqueueRequest(params, false, resolve as (value: unknown) => void, reject);
      });
    },
    [enqueueRequest]
  );

  return {
    ...mutation,
    generateDebounced,
    generateImmediate,
    generateAsync,
  };
}
